import styled, { css, keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { down } from 'styled-breakpoints';

import { TransientProps } from '@common/types';

const tooltipFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-4px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const SidebarContainer = styled.aside`
  ${({ theme }) => css`
    position: fixed;
    top: ${theme.layout.header};
    left: 0;
    bottom: 0;
    width: ${theme.layout.sidebar};
    background: ${theme.colors.background[100]};
    border-right: 1px solid ${theme.colors.background[40]};
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    z-index: 20;

    ${down('lg')} {
      display: none;
    }
  `}
`;

export const NavList = styled.nav`
  display: flex;
  flex-direction: column;
  padding: 0.75rem 0.5rem;
  gap: 0.25rem;
  flex: 1;
`;

export const NavItemWrapper = styled.div`
  position: relative;
`;

type NavItemProps = TransientProps<{
  active: boolean;
}>;

export const NavItem = styled(Link)<NavItemProps>`
  ${({ theme, $active }) => css`
    position: relative;
    width: 2.75rem;
    height: 2.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${theme.radii.md};
    color: ${$active
      ? theme.colors.brand.primary.default
      : theme.colors.foreground[40]};
    background: ${$active ? theme.colors.brand.primary.opaque : 'transparent'};
    transition: all ${theme.transition.default};
    margin: 0 auto;

    svg {
      width: 1.25rem;
      height: 1.25rem;
      flex-shrink: 0;
    }

    &:hover {
      color: ${theme.colors.brand.primary.default};
      background: ${$active
        ? theme.colors.brand.primary.opaque
        : theme.colors.background[60]};
    }

    &:hover + span {
      opacity: 1;
      visibility: visible;
    }
  `}
`;

export const NavButton = styled.button<NavItemProps>`
  ${({ theme, $active }) => css`
    position: relative;
    width: 2.75rem;
    height: 2.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${theme.radii.md};
    border: none;
    cursor: pointer;
    color: ${$active
      ? theme.colors.brand.primary.default
      : theme.colors.foreground[40]};
    background: ${$active ? theme.colors.brand.primary.opaque : 'transparent'};
    transition: all ${theme.transition.default};
    margin: 0 auto;

    svg {
      width: 1.25rem;
      height: 1.25rem;
      flex-shrink: 0;
    }

    &:hover {
      color: ${theme.colors.brand.primary.default};
      background: ${$active
        ? theme.colors.brand.primary.opaque
        : theme.colors.background[60]};
    }

    &:hover + span {
      opacity: 1;
      visibility: visible;
    }
  `}
`;

export const Tooltip = styled.span`
  ${({ theme }) => css`
    position: absolute;
    left: calc(100% + 0.75rem);
    top: 50%;
    transform: translateY(-50%);
    padding: 0.5rem 0.75rem;
    background: ${theme.colors.foreground[100]};
    color: ${theme.colors.background[100]};
    font-size: ${theme.fontSizes.sm};
    font-weight: 500;
    border-radius: ${theme.radii.default};
    white-space: nowrap;
    opacity: 0;
    visibility: hidden;
    transition: opacity ${theme.transition.fast},
      visibility ${theme.transition.fast};
    animation: ${tooltipFadeIn} ${theme.transition.fast} ease-out;
    z-index: 100;
    pointer-events: none;

    &::before {
      content: '';
      position: absolute;
      right: 100%;
      top: 50%;
      transform: translateY(-50%);
      border: 6px solid transparent;
      border-right-color: ${theme.colors.foreground[100]};
    }
  `}
`;

export const Divider = styled.div`
  ${({ theme }) => css`
    height: 1px;
    background: ${theme.colors.background[40]};
    margin: 0.5rem 0.75rem;
  `}
`;

export const BottomSection = styled.div`
  padding: 0.75rem 0.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.background[40]};
`;
